import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { reduxForm, Field, change, getFormValues, isDirty } from 'redux-form';
import { Form } from 'react-bootstrap';
import { isBoolean, keyBy, get } from 'lodash';
import cns from 'classnames';
import BasicSelect from '../../../../../shared/components/formComponents/groups/BasicSelect';
import BasicToggle from '../../../../../shared/components/formComponents/groups/BasicToggle';
import BasicText from '../../../../../shared/components/formComponents/groups/BasicText';
import FormFeedback from '../../../../../shared/components/formComponents/FormFeedback';
import { MENU_STATUS, SSO_OPTIONS } from '../../../../../shared/Constants';
import VerbButton, { VERB_BUTTON_TYPE } from '../../../../../shared/components/VerbButton';
import { getManyLearningFrameworks } from '../../../../../shared/actions/LearningFrameworks';
import { getManyWelcomeAssets } from '../../../../../shared/actions/WelcomeAssets';
import { makeGetObjectsFromStateById } from '../../../../../shared/selectors';
import { RuntimeContext } from '../../../../../../common/RuntimeContext';

const ssoProviderOptions = [
    { label: 'Auth0', value: 'Auth0' },
    { label: 'AzureAD', value: 'Azure' },
    { label: 'SAML', value: 'SAML' }
];
const googleOption = { label: 'Google', value: 'google' };

const ssoProviderOptionsWithGoogle = [...ssoProviderOptions, googleOption];

class CompanyFeaturesForm extends Component {
    static propTypes = {
        changeContentValues: PropTypes.func,
        initialValues: PropTypes.object,
        learningFrameworks: PropTypes.object,
        handleSubmit: PropTypes.func.isRequired,
        submitSucceeded: PropTypes.bool,
        submitFailed: PropTypes.bool,
        onCancel: PropTypes.func,
        getManyLearningFrameworks: PropTypes.func,
        submitting: PropTypes.bool,
        error: PropTypes.string,
        trackLicenses: PropTypes.array,
        currentValues: PropTypes.object,
        company: PropTypes.object,
        dirty: PropTypes.bool,
        toggleShowUnsavedChangesPrompt: PropTypes.func,
        getManyWelcomeAssets: PropTypes.func,
        welcomeAssets: PropTypes.array,
        changeFormValue: PropTypes.func
    };

    static contextType = RuntimeContext;

    constructor(props) {
        super(props);
        this.state = {
            hasCustomDimensions: this.props.initialValues.hasCustomDimensions
        };
    }

    componentDidMount() {
        this.props.getManyLearningFrameworks({ pathsContext: this.context.pathsWeb });
        this.props.getManyWelcomeAssets({ pathsContext: this.context.pathsWeb });
    }

    componentDidUpdate(prevProps) {
        if (prevProps.dirty !== this.props.dirty) {
            return this.props.toggleShowUnsavedChangesPrompt(this.props.dirty);
        }
        return null;
    }

    onBlur = (value, name) => {
        this.setState({ [name]: value });
    };

    _getFormValue(property) {
        return get(this.props.currentValues, `${property}`, '');
    }

    render() {
        const {
            submitting,
            handleSubmit,
            onCancel,
            error,
            learningFrameworks,
            trackLicenses,
            currentValues: { SSOFlow = false, SSOProviders = [] } = {},
            changeContentValues,
            company,
            welcomeAssets,
            changeFormValue
        } = this.props;

        const teamTermOptions = [
            {
                label: 'Teams (Default)',
                value: 'teams'
            },
            {
                label: 'Groups',
                value: 'groups'
            },
            {
                label: 'Departments',
                value: 'departments'
            }
        ];

        const welcomeAssetOptions = welcomeAssets.map((asset) => ({
            label: asset.name,
            value: asset._id
        }));

        const welcomeAssetId = this._getFormValue('welcomeAsset');

        return (
            <Form className={'companyForm container'}>
                <Field
                    name={'SSOFlow'}
                    className={'row company-form-field'}
                    label={'Single Sign On'}
                    inputClassName={'col-12 col-sm-5 no-padding'}
                    component={BasicSelect}
                    placeholder={'None'}
                    description={`Sets whether this company's users will be authenticated using SSO flow.`}
                    companyForm={true}
                    options={SSO_OPTIONS}
                    menuBuffer={15}
                    styleProps={{ horizontal: true }}
                />
                {SSOFlow && (
                    <>
                        <Field
                            component={BasicSelect}
                            name={'SSOProviders'}
                            description={`List of providers for SSO flow`}
                            className={'row company-form-field'}
                            label={'SSO providers'}
                            inputClassName={'col-12 col-sm-5 no-padding'}
                            options={SSOFlow === 'SSO_ALT' ? ssoProviderOptionsWithGoogle : ssoProviderOptions}
                            styleProps={{ horizontal: true }}
                            isMulti={true}
                            menuBuffer={15}
                        />
                        {SSOFlow === 'SSO_ALT' && (
                            <>
                                <Field
                                    name={'SSOSecondaryText'}
                                    className={'row company-form-field'}
                                    label={'Alternative Login Helper Text'}
                                    description={
                                        'This copy will help direct a subset of non-SSO users to login via a traditional email/password combination. For example: “Are you a coach?“ or “Don’t have a company email address”?'
                                    }
                                    component={BasicText}
                                    inputClassName={'col-12 col-sm-5 no-padding'}
                                    styleProps={{
                                        horizontal: true,
                                        inputStyle: { bordered: true, 'full-width': true }
                                    }}
                                />
                            </>
                        )}
                    </>
                )}
                <Field
                    name={'socialAuthentication'}
                    className={cns('row company-form-field', { hidden: SSOProviders.includes(googleOption) })}
                    label={'Social Authentication'}
                    component={BasicToggle}
                    inputClassName={'col-12 col-sm-5'}
                    description={`Sets whether this company's users can use social authentication to sign up or log into the Verb.`}
                    styleProps={{ horizontal: true }}
                    id={'company-features-social-authentication-toggle'}
                />
                <Field
                    name={'disableAutomaticAccountProvisioning'}
                    className={'row company-form-field'}
                    label={'Disable Automatic Account Provisioning'}
                    component={BasicToggle}
                    inputClassName={'col-12 col-sm-5'}
                    description={`Disables this company's users from signing up without a pre-generated account.`}
                    styleProps={{ horizontal: true }}
                    id={'company-features-disable-automatic-account-provisioning-toggle'}
                />
                <Field
                    name={'pathwaysEnabled'}
                    className={'row company-form-field'}
                    label={'Pathways enabled'}
                    component={BasicToggle}
                    inputClassName={'col-12 col-sm-5'}
                    onChange={(newValue) => {
                        if (newValue && !company.pathwayLicenses) {
                            changeContentValues({ field: 'pathwayLicenses', value: [] });
                        }
                    }}
                    description={`Sets whether this company's users can have access and subscribe to pathways.`}
                    styleProps={{ horizontal: true }}
                    id={'company-features-pathways-enabled-toggle'}
                />
                <Field
                    name={`hasCustomDimensions`}
                    className={'row company-form-field custom-dimension-field'}
                    label={'Custom Framework'}
                    component={BasicToggle}
                    inputClassName={'col-12 col-sm-5'}
                    description={'Sets whether the company has custom dimensions.'}
                    styleProps={{ horizontal: true }}
                    id={'company-features-custom-dimensions-toggle'}
                />
                <Field
                    name={'frameworkId'}
                    className={'row company-form-field'}
                    label={' '}
                    options={learningFrameworks}
                    component={BasicSelect}
                    valueKey={'_id'}
                    labelKey={'name'}
                    inputClassName={'col-12 col-sm-5 no-padding'}
                    styleProps={{ horizontal: true, inputStyle: { bordered: true } }}
                />
                <Field
                    name={`frameworkOverviewAsset.name`}
                    className={'row company-form-field'}
                    label={'Custom Framework Navigation Label'}
                    component={BasicText}
                    inputClassName={'col-12 col-sm-5 no-padding'}
                    styleProps={{ horizontal: true, inputStyle: { bordered: true } }}
                />
                <Field
                    name={`frameworkOverviewAsset.id`}
                    className={'row company-form-field'}
                    label={'Custom Framework Overview'}
                    options={trackLicenses}
                    component={BasicSelect}
                    valueKey={'trackId'}
                    labelKey={'trackName'}
                    inputClassName={'col-12 col-sm-5 no-padding'}
                    styleProps={{ horizontal: true, inputStyle: { bordered: true } }}
                />
                <Field
                    name={'enableInviteByEmail'}
                    className={'row company-form-field'}
                    label={'Enable Invite By Email'}
                    component={BasicToggle}
                    inputClassName={'col-12 col-sm-5'}
                    description={'Account owners can invite employees to join Verb via email.'}
                    styleProps={{ horizontal: true }}
                    id={'company-features-invite-by-email-toggle'}
                />
                <Field
                    name={'enablePermalinkInvites'}
                    className={'row company-form-field'}
                    label={'Enable Invites for Team Leads'}
                    component={BasicToggle}
                    inputClassName={'col-12 col-sm-5'}
                    description={'Team Leads owners can invite employees to join Verb via a permalink.'}
                    styleProps={{ horizontal: true }}
                    id={'company-features-enable-permalink-invites-toggle'}
                />
                <Field
                    name={`teamsLabel`}
                    className={'row company-form-field'}
                    label={'Teams: Preferred Term'}
                    options={teamTermOptions}
                    component={BasicSelect}
                    inputClassName={'col-12 col-sm-5 no-padding'}
                    description={'Choose how teams are referred to across the app.'}
                    styleProps={{ horizontal: true, inputStyle: { bordered: true } }}
                    onBlue={this.onBlur}
                />
                <Field
                    name={'customBranding'}
                    className={'row company-form-field'}
                    label={'Customize Brand'}
                    component={BasicToggle}
                    inputClassName={'col-12 col-sm-5'}
                    description={'Allow company administrators to change logo and brand colors.'}
                    styleProps={{ horizontal: true }}
                    id={'company-features-customize-branding-toggle'}
                />
                <Field
                    name={'showWelcomeFlow'}
                    className={'row company-form-field'}
                    label={'Welcome Flow'}
                    component={BasicToggle}
                    inputClassName={'col-12 col-sm-5'}
                    description={'Welcome flow is turned on for the company.'}
                    styleProps={{ horizontal: true }}
                    onChange={() => {
                        if (this._getFormValue('showWelcomeFlow')) {
                            changeFormValue({ field: 'hideWelcomeBanner', value: true });
                        }
                    }}
                    id={'company-features-show-welcome-flow-toggle'}
                />
                <div className={'row'}>
                    <div className={'col-11'}>
                        <Field
                            name={'welcomeAsset'}
                            label={'Welcome Asset'}
                            className={'row company-form-field'}
                            options={welcomeAssetOptions}
                            component={BasicSelect}
                            description={
                                'Sets whether company gets the default welcome asset or a customized welcome asset.'
                            }
                            inputClassName={'col-12 col-sm-5 no-padding'}
                            styleProps={{ horizontal: true, inputStyle: { bordered: true } }}
                        />
                    </div>
                    <div className={'col-1 company-form-field-action-button'}>
                        <VerbButton
                            buttonType={VERB_BUTTON_TYPE.SECONDARY_DETAILS}
                            fontAwesomeIcon={'fa-file-search'}
                            type={'button'}
                            newTab={true}
                            href={`/previewlearningasset/welcome/${welcomeAssetId}`}
                        />
                    </div>
                </div>
                <Field
                    name={'hideWelcomeBanner'}
                    className={'row company-form-field'}
                    label={'Hide Welcome Banner'}
                    component={BasicToggle}
                    inputClassName={'col-12 col-sm-5'}
                    description={'“Welcome to Verb” banner appears on the new Home Page for first-time users.'}
                    styleProps={{ horizontal: true }}
                    disabled={
                        this._getFormValue('hideWelcomeBanner')
                    }
                    id={'company-features-hide-welcome-banner-toggle'}
                />
                <br />
                <div className={'col-12 formButtons'}>
                    <div className={'row'}>
                        <VerbButton
                            fontAwesomeIcon={'fa-save'}
                            textId={'BUTTON_SAVE'}
                            defaultMessage={'SAVE'}
                            onClick={(values) => {
                                this.setState({ formSubmitted: true });
                                handleSubmit(values);
                            }}
                            disabled={submitting}
                        />
                        <VerbButton
                            buttonType={VERB_BUTTON_TYPE.SECONDARY_DEFAULT}
                            fontAwesomeIcon={'fa-ban'}
                            textId={'BUTTON_CANCEL'}
                            defaultMessage={'CANCEL'}
                            onClick={onCancel}
                            disabled={submitting}
                        />
                    </div>
                </div>
                <FormFeedback failMsg={error} className={'col-12'} {...this.props} />
            </Form>
        );
    }
}

export const CompanyFeaturesFormComponent = CompanyFeaturesForm;
export const makeMapStateToProps = () => {
    const getSettings = makeGetObjectsFromStateById('settings');
    const mapStateToProps = (state, ownProps) => {
        const settings = getSettings(state);
        const settingsByKey = keyBy(settings, 'key');
        const {
            hasCustomDimensions,
            enableInviteByEmail,
            enablePermalinkInvites,
            SSOFlow,
            SSOProviders,
            SSOSecondaryText,
            isContentTabEnabled,
            socialAuthentication,
            teamsLabel,
            frameworkId,
            frameworkOverviewAsset,
            customBranding,
            pathwaysEnabled,
            trackLicenses,
            showWelcomeFlow,
            welcomeAsset = settingsByKey.defaultWelcomeFlow && settingsByKey.defaultWelcomeFlow.value,
            hideWelcomeBanner,
            disableAutomaticAccountProvisioning
        } = ownProps.company;
        const { learningFrameworks } = state;
        const currentValues = getFormValues('CompanyFeatures')(state);
        let convertedSSOFlow = SSOFlow;

        if (isBoolean(convertedSSOFlow)) {
            convertedSSOFlow = SSOFlow ? 'SSO' : null;
        }

        const { welcomeAssets: { byId: welcomeAssetsById = {} } = {} } = state;
        const welcomeAssets = Object.values(welcomeAssetsById);

        return {
            dirty: isDirty('CompanyFeatures'),
            initialValues: {
                socialAuthentication,
                hasCustomDimensions,
                enableInviteByEmail,
                enablePermalinkInvites,
                SSOFlow: convertedSSOFlow,
                SSOProviders: ssoProviderOptionsWithGoogle.filter((provider) => (SSOProviders || {})[provider.value]),
                SSOSecondaryText,
                isContentTabEnabled,
                teamsLabel,
                frameworkId,
                customBranding,
                pathwaysEnabled,
                frameworkOverviewAsset,
                showWelcomeFlow,
                welcomeAsset,
                hideWelcomeBanner,
                disableAutomaticAccountProvisioning
            },
            currentValues,
            trackLicenses,
            learningFrameworks: Object.values(learningFrameworks.byId),
            welcomeAssets
        };
    };

    return mapStateToProps;
};

const mapDispatchToProps = (dispatch) => ({
    changeContentValues: (params) => dispatch(change('CompanyContent', params.field, params.value)),
    getManyLearningFrameworks: (params) => dispatch(getManyLearningFrameworks(params)),
    getManyWelcomeAssets: (params) => dispatch(getManyWelcomeAssets(params)),
    changeFormValue: (params) => dispatch(change('CompanyFeatures', params.field, params.value))
});

const CompanyInfoFormValidation = {
    form: 'CompanyFeatures',
    destroyOnUnmount: false,
    enableReinitialize: true,
    validate: ({ SSOFlow, SSOProviders }) => {
        const errors = {};
        const SSOProvidersByValue = (SSOProviders || []).reduce((accum, option) => {
            accum[option.value] = true;
            return accum;
        }, {});
        if (SSOFlow && SSOProviders) {
            if (SSOFlow === SSO_OPTIONS[1].value && SSOProviders.length > 1) {
                errors.SSOProviders = 'Cannot have more than 1 SSO Provider when choosing option, SSO.';
            }

            if (SSOFlow === SSO_OPTIONS[2].value && !SSOProvidersByValue.Auth0 && !SSOProvidersByValue.google) {
                errors.SSOProviders = 'Auth0 must be included when choosing SSO with Alternative Login.';
            }

            if ((SSOFlow === SSO_OPTIONS[2].value || SSOFlow === SSO_OPTIONS[1].value) && !SSOProviders.length) {
                errors.SSOProviders = 'Please choose an SSO option.';
            }
        }

        return errors;
    }
};

export default compose(
    connect(makeMapStateToProps, mapDispatchToProps),
    reduxForm(CompanyInfoFormValidation)
)(CompanyFeaturesForm);
